import { Done, Edit, FileCopyOutlined, Info as InfoIcon } from "@mui/icons-material";
import React, { useEffect, useRef, useState } from "react";
import { ExternalLink } from "../../../common/components/ExternalLink";
import { NetworkRequest } from "../../../common/models";
import {
  ConsentManagementScanVendorDto,
  EmploymentGroupNotices,
  PublicOrganizationNoticeDto,
  PublicPrivacyCenterDto,
} from "../../../common/service/server/dto/PublicOrganizationNoticeDto";
import { OrganizationPublicId } from "../../../common/service/server/Types";
import { Para } from "../../components/typography/Para";
import { Paras } from "../../components/typography/Paras";
import { useOrganizationVendors, usePrimaryOrganization } from "../../hooks";
import { usePrivacyCenter } from "../../hooks/usePrivacyCenter";
import {
  usePrimaryOrganizationId,
  usePublicOrganizationNotices,
} from "../../hooks/useOrganization";
import { frontendPathToUrl, RouteHelpers, Routes } from "../../root/routes";
import { PrivacyCenterUrls, removeUrlProtocol } from "../../util/privacyCenterUtil";
import { PrivacyNoticeActions } from "../../components/organisms/privacy-notice/Foo";
import { OptOutIconSelector } from "../../components/organisms/OptOutIconSelector";
import { Link, useHistory } from "react-router-dom";
import { Callout, CalloutVariant } from "../../components/Callout";
import { SurveyAnswerFn, SurveyAnswers, useSurvey } from "../../../common/hooks/api/useSurvey";
import {
  useWebsiteAudit,
  WebsiteAudit,
} from "../../components/organisms/website-audit/WebsiteAudit";
import { doesGppSignalApplyForVendor } from "../data-map/vendor-documentation/VendorDocumentationPage";
import { OrganizationDataRecipientDto } from "../../../common/service/server/dto/OrganizationDataRecipientDto";
import { AlertCard, SimpleCheckboxControlled } from "@truevault/aquilo";
import { RefreshFn } from "../../../common/hooks/api";
import { BUSINESS_SURVEY_NAME, USES_GOOGLE_TAG_MANAGER_SLUG } from "../../surveys/businessSurvey";

type UsePrivacyNoticeChecklistItems = {
  checklistProps: PrivacyNoticeChecklistItemsProps;
  ready: boolean;
};

export const usePrivacyNoticeChecklistItems = (
  orgId: OrganizationPublicId,
): UsePrivacyNoticeChecklistItems => {
  const [ready, setReady] = useState(false);

  // network fetches
  const [notices, noticesRequest] = usePublicOrganizationNotices(orgId);

  // Effects
  useEffect(() => {
    if (NetworkRequest.areFinished(noticesRequest)) {
      setReady(true);
    }
  }, [noticesRequest]);

  return {
    checklistProps: { notices },
    ready,
  };
};

export type PrivacyNoticeChecklistItemsProps = {
  notices: PublicOrganizationNoticeDto;
  instructions?: boolean;
  visible?: PublishNoticesTabs[];
  stayCompliant?: boolean;
  isHr?: boolean;
  isPublicView?: boolean;
  showColoradoBiometricNoticeEditor?: boolean;
  hasCompletedColoradoBiometricNotice?: boolean;
  coloradoBiometricNoticeUrl?: string;
};

export type PublishNoticesTabs = "web" | "mobile" | "in-store" | "employment";
export const DEVELOPER_INSTRUCTIONS_SURVEY_NAME = "developer-instruction-survey";

export const DevInstructionsSurveySlug = "finished-dev-instructions";

export const DevInstructionsInStoreNoticeSlug = "finished-in-store-instructions";
export const DevInstructionsWebNoticeSlug = "finished-web-instructions";
export const DevInstructionsMobileNoticeSlug = "finished-mobile-instructions";
export const DevInstructionsHrNoticeSlug = "finished-hr-instructions";

export const DevInstructionsConsentManagementNoticeSlug =
  "finished-consent-management-instructions";
export const DevInstructionsConsentManagementFinishedVendorIdsSlug =
  "finished-consent-management-vendor-ids";

// The slugs below only track a user's progress through the GTM consent settings; they must stay
// out of `getDevInstructionsCompletionStatus`. Only the Targeted Advertising list
// (`finished-consent-management-vendor-ids` above) feeds section completion, because that is the
// list orgs checked off back when it was the section's only completion mechanism.
const DevInstructionsConsentManagementAdvertisingVendorIdsSlug =
  "finished-consent-management-advertising-vendor-ids";
const DevInstructionsConsentManagementAnalyticsVendorIdsSlug =
  "finished-consent-management-analytics-vendor-ids";
const DevInstructionsConsentManagementPreferencesVendorIdsSlug =
  "finished-consent-management-preferences-vendor-ids";

const isSurveyAnswerTrue = (answers: SurveyAnswers, slug: string) =>
  answers && !!answers[slug] && answers[slug] === "true";

const isSurveyAnswerFalse = (answers: SurveyAnswers, slug: string) =>
  answers && !!answers[slug] && answers[slug] === "false";

const parseCompletedVendorIds = (answer?: string): string[] => {
  if (!answer) return [];

  try {
    const parsed: unknown = JSON.parse(answer);
    return Array.isArray(parsed)
      ? parsed.filter((value): value is string => typeof value === "string")
      : [];
  } catch {
    return [];
  }
};

const filterNonGppVendorsForConsentManagement = (allVendors: OrganizationDataRecipientDto[]) => {
  // In Consent Management section, show vendors that are either missing GPP signal or have marked GPP signal as not respected/unknown
  return (allVendors || [])
    .filter((v) => doesGppSignalApplyForVendor(v))
    .filter(
      (v) => !v.gppSignalStatus || v.gppSignalStatus === "GPP_SIGNAL_NOT_RESPECTED_OR_UNKNOWN",
    );
};

export const getDevInstructionsCompletionStatus = (
  devSurveyAnswers: SurveyAnswers,
  notices: PublicOrganizationNoticeDto,
  allVendors: OrganizationDataRecipientDto[],
): {
  didFinishInstoreInstructions: boolean;
  didFinishWebInstructions: boolean;
  didFinishConsentManagementInstructions: boolean;
  didFinishMobileInstructions: boolean;
  allDevInstructionsNoticesFinished: boolean;
} => {
  if (!devSurveyAnswers || !notices) {
    return {
      didFinishInstoreInstructions: false,
      didFinishWebInstructions: false,
      didFinishConsentManagementInstructions: false,
      didFinishMobileInstructions: false,
      allDevInstructionsNoticesFinished: false,
    };
  }

  const { hasPhysicalLocation, hasMobileApplication } = notices;

  const didFinishInstoreInstructions =
    !hasPhysicalLocation || isSurveyAnswerTrue(devSurveyAnswers, DevInstructionsInStoreNoticeSlug);
  const didFinishWebInstructions = isSurveyAnswerTrue(
    devSurveyAnswers,
    DevInstructionsWebNoticeSlug,
  );
  const didFinishMobileInstructions =
    !hasMobileApplication || isSurveyAnswerTrue(devSurveyAnswers, DevInstructionsMobileNoticeSlug);

  const nonGppVendorsForConsentManagement = filterNonGppVendorsForConsentManagement(
    allVendors || [],
  );
  const showConsentManagementNotice = nonGppVendorsForConsentManagement.length > 0;
  const completedConsentManagementVendorIds = parseCompletedVendorIds(
    devSurveyAnswers[DevInstructionsConsentManagementFinishedVendorIdsSlug],
  );
  // Note: when updating this, also update `checkConsentManagementDevInstructionsCompletionStatus`
  // in `OrganizationOnboardingProgressService.kt`
  const didFinishConsentManagementInstructions =
    isSurveyAnswerTrue(devSurveyAnswers, DevInstructionsConsentManagementNoticeSlug) ||
    !showConsentManagementNotice ||
    nonGppVendorsForConsentManagement.every((v) =>
      completedConsentManagementVendorIds.includes(v.vendorId),
    );

  // Note: don't include `didFinishConsentManagementInstructions` since it is not a one-time onboarding step
  const allDevInstructionsNoticesFinished =
    didFinishInstoreInstructions && didFinishWebInstructions && didFinishMobileInstructions;

  return {
    didFinishInstoreInstructions,
    didFinishWebInstructions,
    didFinishConsentManagementInstructions,
    didFinishMobileInstructions,
    allDevInstructionsNoticesFinished,
  };
};

export const PrivacyNoticeChecklistItems = ({
  notices,
  isHr = false,
  isPublicView = false,
  showColoradoBiometricNoticeEditor = false,
  hasCompletedColoradoBiometricNotice = false,
  coloradoBiometricNoticeUrl,
}: PrivacyNoticeChecklistItemsProps) => {
  const history = useHistory();
  const [org] = usePrimaryOrganization();
  const {
    privacyCenter,
    isGoogleAnalytics,
    isSelling,
    isSharing,
    showFinancialIncentive,
    hasJobApplicants,
    hasPhysicalLocation,
    hasMobileApplication,
    employmentGroupNotices: employmentGroups,
  } = notices;

  const [allVendors, ,] = useOrganizationVendors(
    org?.id,
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ false,
    /* excludeIncomplete = */ undefined,
    /* context = */ undefined,
    /* onlyUseSettled = */ true,
  );

  const empNotices = (employmentGroups && employmentGroups.length > 0) || hasJobApplicants;

  const websiteAudit = useWebsiteAudit();

  const orgId = usePrimaryOrganizationId();

  const [fullPrivacyCenter] = usePrivacyCenter(orgId);

  const { answers: businessSurveyAnswers } = useSurvey(orgId, BUSINESS_SURVEY_NAME);

  const {
    answers: devSurveyAnswers,
    refresh: refreshDevSurveyAnswers,
    setAnswer: setDevSurveyAnswer,
  } = useSurvey(orgId, DEVELOPER_INSTRUCTIONS_SURVEY_NAME);
  const setAnswerRef = useRef(setDevSurveyAnswer);
  const isMarkingDevInstructionsCompleteRef = useRef(false);
  setAnswerRef.current = setDevSurveyAnswer;

  const {
    didFinishInstoreInstructions,
    didFinishWebInstructions,
    didFinishConsentManagementInstructions,
    didFinishMobileInstructions,
    allDevInstructionsNoticesFinished,
  } = getDevInstructionsCompletionStatus(devSurveyAnswers, notices, allVendors);
  const consentManagementNonGppVendors = filterNonGppVendorsForConsentManagement(allVendors || []);

  const didFinishHrNoticeInstructions =
    !empNotices || isSurveyAnswerTrue(devSurveyAnswers, DevInstructionsHrNoticeSlug);
  const isDevInstructionsMarkedComplete = isSurveyAnswerTrue(
    devSurveyAnswers,
    DevInstructionsSurveySlug,
  );

  // Consent Implementation is ongoing work rather than a one-time step, so it's the section a
  // returning user is most likely revisiting — an already-complete one starts expanded. Only
  // completing it during this visit collapses it, the way the other sections do.
  const [justCompletedConsentInstructions, setJustCompletedConsentInstructions] = useState(false);

  useEffect(() => {
    if (
      allDevInstructionsNoticesFinished &&
      !isDevInstructionsMarkedComplete &&
      !isMarkingDevInstructionsCompleteRef.current
    ) {
      isMarkingDevInstructionsCompleteRef.current = true;
      void setAnswerRef
        .current(DevInstructionsSurveySlug, "true")
        .catch(() => undefined)
        .finally(() => {
          isMarkingDevInstructionsCompleteRef.current = false;
        });
    }
  }, [allDevInstructionsNoticesFinished, isDevInstructionsMarkedComplete]);

  return (
    <div>
      <div className="tw-boundary">
        <p className="mt-8 font-medium text-neutral-800">
          {isHr === true
            ? "View the instructions below for publishing your HR notices"
            : "View the instructions below for publishing the notices on your site"}
        </p>
      </div>

      {!isPublicView && (
        <div
          style={{
            marginTop: "48px",
            borderRadius: "10px",
            boxShadow: "0px 2px 12px 0px rgba(0, 0, 0, 0.12)",
            padding: "24px",
            width: "945px",
          }}
        >
          <div style={{ fontSize: "18px", fontWeight: 500, color: "#332E42" }}>
            Have a developer? Share the instructions with them
          </div>
          <UrlNoticeAction
            url={frontendPathToUrl(
              RouteHelpers.instructions.id(
                isHr === true ? org?.hrInstructionsId : org?.devInstructionsId,
              ),
            )}
          />
        </div>
      )}

      {isHr && empNotices && (
        <>
          <Callout
            variant={CalloutVariant.LightPurpleBordered}
            collapsible
            collapsed={didFinishHrNoticeInstructions}
            title={"Employment Notices"}
            className="callout--centerAction"
            action={
              !isPublicView && (
                <InlineBadge
                  badgeType={didFinishHrNoticeInstructions ? "complete" : "finish-set-up"}
                />
              )
            }
            // @ts-ignore sx is a prop on all Material UI components, see https://mui.com/system/getting-started/the-sx-prop/
            sx={{
              width: "945px",
            }}
            style={{
              marginTop: "48px",
            }}
          >
            <EmploymentNotices
              privacyCenter={privacyCenter}
              employmentGroups={employmentGroups}
              hasJobApplicants={hasJobApplicants}
            />
          </Callout>

          {!didFinishHrNoticeInstructions && !isPublicView && (
            <MarkAsCompletedButton
              onClick={async () => {
                await setDevSurveyAnswer(DevInstructionsHrNoticeSlug, "true");
              }}
            />
          )}
        </>
      )}

      {isHr && showColoradoBiometricNoticeEditor && (
        <Callout
          variant={CalloutVariant.LightPurpleBordered}
          collapsible
          collapsed={hasCompletedColoradoBiometricNotice}
          title="Colorado Biometric Identifiers Notice for Employees and Contractors"
          className="callout--centerAction"
          action={
            !isPublicView && (
              <InlineBadge
                badgeType={hasCompletedColoradoBiometricNotice ? "complete" : "finish-set-up"}
              />
            )
          }
          // @ts-ignore sx is a prop on all Material UI components, see https://mui.com/system/getting-started/the-sx-prop/
          sx={{
            width: "945px",
          }}
          style={{
            marginTop: "48px",
          }}
        >
          <div className="compliance-checklist--notices">
            <div className="compliance-checklist--notices-items">
              <ol className="custom p-0">
                <PrivacyNoticeTask
                  description="Review and edit the biometric notice for your Colorado employees and contractors"
                  helpText={
                    <>
                      Include the notice at or before the point where your business collects
                      personal information from Colorado employees and/or contractors, such as
                      accompanying an offer letter; included with onboarding materials; as an
                      addendum or enclosure with a contract (e.g., for contractors); or in an
                      employee handbook.
                    </>
                  }
                >
                  <PrivacyNoticeActions
                    content={
                      <span className="text-t1 text-mono">
                        Biometric Identifiers Notice to Colorado Employees and Contractors
                      </span>
                    }
                    actions={[
                      {
                        id: "edit-colorado-employee-biometric-notice",
                        content: (
                          <>
                            <Edit /> Edit Notice
                          </>
                        ),
                        onClick: () => history.push(Routes.hrCenter.coloradoBiometricNotice),
                      },
                      ...(coloradoBiometricNoticeUrl
                        ? [
                            getCopyAction(
                              "copy-colorado-employee-biometric-notice-url",
                              "Copy URL",
                              coloradoBiometricNoticeUrl,
                            ),
                          ]
                        : []),
                    ]}
                  />
                </PrivacyNoticeTask>
              </ol>
            </div>
          </div>
        </Callout>
      )}

      {!isHr && (
        <Callout
          variant={CalloutVariant.LightPurpleBordered}
          collapsible
          collapsed={didFinishWebInstructions}
          title={"Website Implementation"}
          id="web-notices-wrapper"
          className="callout--centerAction"
          action={
            !isPublicView && (
              <InlineBadge badgeType={didFinishWebInstructions ? "complete" : "finish-set-up"} />
            )
          }
          // @ts-ignore sx is a prop on all Material UI components, see https://mui.com/system/getting-started/the-sx-prop/
          sx={{
            width: "945px",
          }}
          style={{
            marginTop: "48px",
          }}
        >
          <WebInstructions
            isGoogleAnalytics={isGoogleAnalytics}
            isSelling={isSelling}
            isSharing={isSharing}
            canShowGANudge={didFinishWebInstructions}
            isGAMeasurementIdKnown={(privacyCenter.gaMeasurementId ?? "") !== ""}
            privacyCenter={privacyCenter}
            logoLinkUrl={fullPrivacyCenter?.logoLinkUrl ?? ""}
            showFinancialIncentive={showFinancialIncentive}
          />

          {!didFinishWebInstructions && !isPublicView && (
            <MarkAsCompletedButton
              onClick={async () => {
                await setDevSurveyAnswer(DevInstructionsWebNoticeSlug, "true");

                let nextSectionToScrollTo = hasMobileApplication ? "mobile" : "consent-management";
                if (nextSectionToScrollTo) {
                  setTimeout(() => {
                    document
                      .getElementById(`${nextSectionToScrollTo}-notices-wrapper`)
                      ?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                  }, 1000);
                }
              }}
              width="905px"
            />
          )}
        </Callout>
      )}

      {hasMobileApplication && !isHr && (
        <Callout
          variant={CalloutVariant.LightPurpleBordered}
          collapsible
          collapsed={didFinishMobileInstructions ? true : !didFinishWebInstructions}
          title={"Mobile App Implementation"}
          id="mobile-notices-wrapper"
          className="callout--centerAction"
          action={
            !isPublicView && (
              <InlineBadge badgeType={didFinishMobileInstructions ? "complete" : "finish-set-up"} />
            )
          }
          // @ts-ignore sx is a prop on all Material UI components, see https://mui.com/system/getting-started/the-sx-prop/
          sx={{
            width: "945px",
          }}
          style={{
            marginTop: "24px",
          }}
        >
          <MobileNotices
            privacyCenter={privacyCenter}
            isSelling={isSelling}
            isSharing={isSharing}
          />

          {!didFinishMobileInstructions && !isPublicView && (
            <MarkAsCompletedButton
              onClick={async () => {
                await setDevSurveyAnswer(DevInstructionsMobileNoticeSlug, "true");

                let nextSectionToScrollTo = "consent-management";
                if (nextSectionToScrollTo) {
                  setTimeout(() => {
                    document
                      .getElementById(`${nextSectionToScrollTo}-notices-wrapper`)
                      ?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                  }, 1000);
                }
              }}
              width="905px"
            />
          )}
        </Callout>
      )}

      {!isHr && (
        <>
          <Callout
            variant={CalloutVariant.LightPurpleBordered}
            collapsible
            // Collapses on completion only within the visit that completed it — see
            // `justCompletedConsentInstructions`.
            collapsed={
              justCompletedConsentInstructions ||
              !didFinishWebInstructions ||
              !didFinishMobileInstructions
            }
            title={"Consent Implementation"}
            id="consent-management-notices-wrapper"
            className="callout--centerAction"
            action={
              !isPublicView &&
              consentManagementNonGppVendors.length > 0 && (
                <InlineBadge
                  badgeType={didFinishConsentManagementInstructions ? "complete" : "finish-set-up"}
                />
              )
            }
            // @ts-ignore sx is a prop on all Material UI components, see https://mui.com/system/getting-started/the-sx-prop/
            sx={{
              width: "945px",
            }}
            style={{
              marginTop: "24px",
            }}
          >
            <ConsentManagementNotices
              consentManagementNonGppVendors={consentManagementNonGppVendors}
              consentManagementScanVendors={notices.consentManagementScanVendors ?? []}
              businessSurveyAnswers={businessSurveyAnswers}
              devSurveyAnswers={devSurveyAnswers}
              refreshDevSurveyAnswers={refreshDevSurveyAnswers}
              setDevSurveyAnswer={setDevSurveyAnswer}
              isPublicView={isPublicView}
            />

            {/* `didFinishConsentManagementInstructions` is already true when there are no non-GPP
                vendors, so this button appears exactly where the badge does. */}
            {!didFinishConsentManagementInstructions && !isPublicView && (
              <MarkAsCompletedButton
                onClick={async () => {
                  await setDevSurveyAnswer(DevInstructionsConsentManagementNoticeSlug, "true");
                  setJustCompletedConsentInstructions(true);

                  let nextSectionToScrollTo = hasPhysicalLocation ? "in-store" : null;
                  if (nextSectionToScrollTo) {
                    setTimeout(() => {
                      document
                        .getElementById(`${nextSectionToScrollTo}-notices-wrapper`)
                        ?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                    }, 1000);
                  }
                }}
                width="905px"
              />
            )}
          </Callout>
        </>
      )}

      {hasPhysicalLocation && !isHr && (
        <>
          <div className="tw-boundary">
            <p className="font-medium text-neutral-800 mt-8">
              View the instructions below for publishing your notices in your store
            </p>
          </div>

          <Callout
            variant={CalloutVariant.LightPurpleBordered}
            collapsible
            collapsed={
              didFinishInstoreInstructions
                ? true
                : !didFinishWebInstructions || !didFinishMobileInstructions
            }
            title={"In-Store Notices"}
            id="in-store-notices-wrapper"
            className="callout--centerAction"
            action={
              !isPublicView && (
                <InlineBadge
                  badgeType={didFinishInstoreInstructions ? "complete" : "finish-set-up"}
                />
              )
            }
            // @ts-ignore sx is a prop on all Material UI components, see https://mui.com/system/getting-started/the-sx-prop/
            sx={{
              width: "945px",
            }}
            style={{
              marginTop: "24px",
            }}
          >
            <InStoreNotices privacyCenter={privacyCenter} />

            {!didFinishInstoreInstructions && !isPublicView && (
              <MarkAsCompletedButton
                onClick={async () => {
                  await setDevSurveyAnswer(DevInstructionsInStoreNoticeSlug, "true");
                }}
                width="905px"
              />
            )}
          </Callout>
        </>
      )}

      {!isPublicView && !isHr && (
        <>
          {allDevInstructionsNoticesFinished && <CompletedButtonPlaceholder />}

          <div style={{ marginTop: "48px", color: "#332E42", fontSize: "18px", fontWeight: 900 }}>
            All done? We'll scan your site to ensure all required links are present
          </div>

          <WebsiteAudit
            useNewDesign={true}
            autoFocus={false}
            startScanDisabled={!allDevInstructionsNoticesFinished}
            {...websiteAudit.props}
          />
        </>
      )}
    </div>
  );
};

const InlineBadge = ({ badgeType }: { badgeType: "finish-set-up" | "complete" }) => {
  const bgColor = badgeType === "finish-set-up" ? "#ffd230" : "#82E3BC";
  const text = badgeType === "finish-set-up" ? "Finish Set Up" : "Complete";
  return (
    <div
      style={{
        backgroundColor: bgColor,
        color: "rgba(0, 0, 0, 0.87)",
        borderRadius: "100px",
        fontSize: "14px",
        fontWeight: "500",
        padding: "6px 12px",
      }}
    >
      {text}
    </div>
  );
};

export const MarkAsCompletedButton = ({
  onClick,
  width,
  disabled,
  label = "Mark as Completed",
}: {
  onClick: () => Promise<void>;
  width?: string;
  disabled?: boolean;
  label?: string;
}) => {
  return (
    <div
      style={{
        border: "1px solid #918CA1",
        borderRadius: "5px",
        padding: "10px 0",
        width: width || "945px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        marginTop: "48px",
      }}
      onClick={!disabled ? onClick : undefined}
    >
      {!disabled && (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <mask
            id="mask0_5535_712"
            style={{ maskType: "alpha" }}
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="24"
            height="24"
          >
            <rect width="24" height="24" fill="#D9D9D9" />
          </mask>
          <g mask="url(#mask0_5535_712)">
            <path
              d="M12 22C10.6167 22 9.31667 21.7375 8.1 21.2125C6.88333 20.6875 5.825 19.975 4.925 19.075C4.025 18.175 3.3125 17.1167 2.7875 15.9C2.2625 14.6833 2 13.3833 2 12C2 10.6167 2.2625 9.31667 2.7875 8.1C3.3125 6.88333 4.025 5.825 4.925 4.925C5.825 4.025 6.88333 3.3125 8.1 2.7875C9.31667 2.2625 10.6167 2 12 2C13.3833 2 14.6833 2.2625 15.9 2.7875C17.1167 3.3125 18.175 4.025 19.075 4.925C19.975 5.825 20.6875 6.88333 21.2125 8.1C21.7375 9.31667 22 10.6167 22 12C22 13.3833 21.7375 14.6833 21.2125 15.9C20.6875 17.1167 19.975 18.175 19.075 19.075C18.175 19.975 17.1167 20.6875 15.9 21.2125C14.6833 21.7375 13.3833 22 12 22ZM12 20C14.2333 20 16.125 19.225 17.675 17.675C19.225 16.125 20 14.2333 20 12C20 9.76667 19.225 7.875 17.675 6.325C16.125 4.775 14.2333 4 12 4C9.76667 4 7.875 4.775 6.325 6.325C4.775 7.875 4 9.76667 4 12C4 14.2333 4.775 16.125 6.325 17.675C7.875 19.225 9.76667 20 12 20Z"
              fill="#1C1B1F"
            />
          </g>
        </svg>
      )}
      <div
        style={{
          marginLeft: "10px",
          color: disabled ? "#918ca1" : "#1A1726",
          fontWeight: disabled ? undefined : 500,
        }}
      >
        {label}
      </div>
    </div>
  );
};

export const CompletedButtonPlaceholder = () => {
  return (
    <div
      style={{
        backgroundColor: "#F3F0FE",
        border: "1px solid #5331C6",
        borderRadius: "5px",
        padding: "10px 0",
        width: "945px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        marginTop: "48px",
      }}
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <mask
          id="mask0_5537_1623"
          style={{ maskType: "alpha" }}
          maskUnits="userSpaceOnUse"
          x="0"
          y="0"
          width="24"
          height="24"
        >
          <rect width="24" height="24" fill="#D9D9D9" />
        </mask>
        <g mask="url(#mask0_5537_1623)">
          <path
            d="M10.6 16.6L17.65 9.55L16.25 8.15L10.6 13.8L7.75 10.95L6.35 12.35L10.6 16.6ZM12 22C10.6167 22 9.31667 21.7375 8.1 21.2125C6.88333 20.6875 5.825 19.975 4.925 19.075C4.025 18.175 3.3125 17.1167 2.7875 15.9C2.2625 14.6833 2 13.3833 2 12C2 10.6167 2.2625 9.31667 2.7875 8.1C3.3125 6.88333 4.025 5.825 4.925 4.925C5.825 4.025 6.88333 3.3125 8.1 2.7875C9.31667 2.2625 10.6167 2 12 2C13.3833 2 14.6833 2.2625 15.9 2.7875C17.1167 3.3125 18.175 4.025 19.075 4.925C19.975 5.825 20.6875 6.88333 21.2125 8.1C21.7375 9.31667 22 10.6167 22 12C22 13.3833 21.7375 14.6833 21.2125 15.9C20.6875 17.1167 19.975 18.175 19.075 19.075C18.175 19.975 17.1167 20.6875 15.9 21.2125C14.6833 21.7375 13.3833 22 12 22ZM12 20C14.2333 20 16.125 19.225 17.675 17.675C19.225 16.125 20 14.2333 20 12C20 9.76667 19.225 7.875 17.675 6.325C16.125 4.775 14.2333 4 12 4C9.76667 4 7.875 4.775 6.325 6.325C4.775 7.875 4 9.76667 4 12C4 14.2333 4.775 16.125 6.325 17.675C7.875 19.225 9.76667 20 12 20Z"
            fill="#1C1B1F"
          />
        </g>
      </svg>
      <div style={{ marginLeft: "10px", color: "#130052", fontWeight: 500 }}>Completed</div>
    </div>
  );
};

type MobileNoticesProps = {
  privacyCenter: PublicPrivacyCenterDto;

  isSelling: boolean;
  isSharing: boolean;
};

const MobileNotices = ({ privacyCenter, isSelling, isSharing }: MobileNoticesProps) => {
  return (
    <div className="compliance-checklist--notices">
      <div className="compliance-checklist--notices-items">
        <ol className="custom p-0">
          <PrivacyNoticeTask
            description={
              <>
                On the mobile app’s download page (Apple App store, Google Play), update the link to
                your business’s Privacy Policy
              </>
            }
          >
            <UrlNoticeAction
              id="copy-privacy-policy-mobile-download"
              label="Privacy Policy"
              url={PrivacyCenterUrls.getPrivacyPolicyUrl(privacyCenter)}
            />
          </PrivacyNoticeTask>

          <PrivacyNoticeTask
            description={<>Update app description to include other privacy notices</>}
            helpText={
              <>Paste the text below into the app description on your app’s download page.</>
            }
          >
            <PrivacyNoticeActions
              content={
                <span className="text-t1 text-font-mono" style={{ whiteSpace: "pre-wrap" }}>
                  {getMobilePrivacyNoticesText(
                    PrivacyCenterUrls.getCAPrivacyNoticeUrl(privacyCenter),
                    privacyCenter,
                    isSelling,
                    isSharing,
                  )}
                </span>
              }
              actions={[
                getCopyAction(
                  "update-mobile-app-description-privacy-notices-copy-text",
                  "copy text",
                  getMobilePrivacyNoticesText(
                    PrivacyCenterUrls.getCAPrivacyNoticeUrl(privacyCenter),
                    privacyCenter,
                    isSelling,
                    isSharing,
                  ),
                ),
              ]}
            />
          </PrivacyNoticeTask>
          <PrivacyNoticeTask
            description={<>Within your business’s mobile app, post (or update) the links below</>}
            helpText={
              <>
                For example, add links to each notice in the app’s settings menu. Title the links
                using the exact language below.
              </>
            }
          >
            <UrlNoticeAction
              label="California Privacy Notice"
              url={PrivacyCenterUrls.getCAPrivacyNoticeUrl(privacyCenter)}
            />
            <UrlNoticeAction
              id="copy-privacy-policy-mobile-app"
              label="Privacy Policy"
              url={PrivacyCenterUrls.getPrivacyPolicyUrl(privacyCenter)}
            />

            {showOptOutNotice(isSelling, isSharing) && (
              <UrlNoticeAction
                url={PrivacyCenterUrls.getOptOutUrl(privacyCenter, isSelling, isSharing)}
                label={optOutLabel()}
              />
            )}
          </PrivacyNoticeTask>
          <PrivacyNoticeTask
            description={<>Implement consent management in your mobile app</>}
            helpText={
              <>
                Our cross-platform consent management API records a consumer's consent choices. Your
                app will query them before interfacing with 3rd party APIs or SDKs to ensure
                opt-outs are respected.
                <br />
                <br />
                See the implementation guide at{" "}
                <ExternalLink
                  href="https://developers.truevault.com/docs/cross-platform-consent/overview"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  developers.truevault.com/docs/cross-platform-consent/overview
                </ExternalLink>
              </>
            }
          />
        </ol>
      </div>
    </div>
  );
};

const ConsentManagementNotices = ({
  consentManagementNonGppVendors,
  consentManagementScanVendors,
  businessSurveyAnswers,
  devSurveyAnswers,
  refreshDevSurveyAnswers,
  setDevSurveyAnswer,
  isPublicView,
}: {
  consentManagementNonGppVendors: OrganizationDataRecipientDto[];
  consentManagementScanVendors: ConsentManagementScanVendorDto[];
  businessSurveyAnswers: SurveyAnswers;
  devSurveyAnswers: SurveyAnswers;
  refreshDevSurveyAnswers: RefreshFn;
  setDevSurveyAnswer: SurveyAnswerFn;
  isPublicView: boolean;
}) => {
  const completedVendorIdsFor = (slug: string): string[] =>
    parseCompletedVendorIds(devSurveyAnswers[slug]);

  const advertisingVendors = consentManagementScanVendors.filter((vendor) =>
    vendor.consentCategories.includes("ADVERTISING"),
  );
  const analyticsVendors = consentManagementScanVendors.filter((vendor) =>
    vendor.consentCategories.includes("ANALYTICS"),
  );
  const preferencesVendors = consentManagementScanVendors.filter((vendor) =>
    vendor.consentCategories.includes("PERSONALIZATION"),
  );

  const scanVendorListItems = (vendors: ConsentManagementScanVendorDto[]) =>
    vendors.map((vendor) => ({
      id: vendor.id,
      name: vendor.name,
      logoUrl: vendor.logoUrl,
    }));

  const optOutVendorListItems = consentManagementNonGppVendors.map((vendor) => ({
    id: vendor.id,
    completionId: vendor.vendorId,
    name: vendor.name,
    logoUrl: vendor.logoUrl,
  }));

  const makeToggleVendor = (slug: string) =>
    isPublicView
      ? undefined
      : async (vendorId: string) => {
          const completedVendorIds = completedVendorIdsFor(slug);
          const newCompletedVendorIds = completedVendorIds.includes(vendorId)
            ? completedVendorIds.filter((completedVendorId) => completedVendorId !== vendorId)
            : [...completedVendorIds, vendorId];
          await setDevSurveyAnswer(slug, JSON.stringify(newCompletedVendorIds));
          await refreshDevSurveyAnswers();
        };

  // Only look for an explicit "No" answer to the GTM survey question, i.e. unanswered implies they might be using GTM
  const isNotUsingGoogleTagManager = isSurveyAnswerFalse(
    businessSurveyAnswers,
    USES_GOOGLE_TAG_MANAGER_SLUG,
  );

  return (
    <div className="compliance-checklist--notices">
      <div className="compliance-checklist--notices-items">
        <div className="tw-boundary">
          <AlertCard
            variant="info"
            description={
              isNotUsingGoogleTagManager ? (
                <>
                  <p>
                    TrueVault records a visitor's consent choices, but your website must use those
                    choices to control when third-party tags load.
                  </p>
                  <br />
                  <p>
                    Because you indicated on your{" "}
                    <a
                      className="text-blue-600 underline"
                      href={Routes.organization.settings.businessSurvey}
                    >
                      Business Profile
                    </a>{" "}
                    that you do not use Google Tag Manager, follow the linked instructions for a
                    different tag manager below. If you are now using Google Tag Manager, update
                    your{" "}
                    <a
                      className="text-blue-600 underline"
                      href={Routes.organization.settings.businessSurvey}
                    >
                      Business Profile
                    </a>{" "}
                    and return to this page to see Google Tag Manager-specific instructions
                  </p>
                </>
              ) : (
                <>
                  TrueVault records a visitor's consent choices, but your website must use those
                  choices to control when third-party tags load. We recommend Google Tag Manager
                  (GTM) because it can evaluate consent before requesting a tag. Start with our{" "}
                  <ExternalLink
                    className="text-blue-600 underline"
                    href={CONSENT_MANAGEMENT_GETTING_STARTED_URL}
                    target="_blank"
                  >
                    Consent Management Getting Started guide
                  </ExternalLink>{" "}
                  to choose the integration that matches your site.
                </>
              )
            }
            containerClasses="mt-0"
          />
        </div>
        <ol className="custom p-0" style={{ marginTop: "16px" }}>
          <PrivacyNoticeTask
            description="Choose how your site manages tags"
            helpText={
              <Paras>
                <Para paragraph className="text-t1">
                  Follow the instructions for every system that loads tags on your website:
                </Para>
                <ul className="list-disc pl-6 text-t1">
                  <li>
                    <ExternalLink
                      className="text-link"
                      href={GTM_SETUP_OVERVIEW_URL}
                      target="_blank"
                    >
                      Google Tag Manager (recommended)
                    </ExternalLink>
                  </li>
                  <li>
                    <ExternalLink
                      className="text-link"
                      href={SHOPIFY_CONSENT_HELPSCOUT_URL}
                      target="_blank"
                    >
                      Shopify
                    </ExternalLink>
                  </li>
                  <li>
                    <ExternalLink
                      className="text-link"
                      href={BIGCOMMERCE_CONSENT_URL}
                      target="_blank"
                    >
                      BigCommerce
                    </ExternalLink>
                  </li>
                  <li>
                    <ExternalLink
                      className="text-link"
                      href={CONSENT_MANAGEMENT_WITHOUT_GTM_URL}
                      target="_blank"
                    >
                      Tags installed directly on your site
                    </ExternalLink>
                  </li>
                </ul>
              </Paras>
            }
          />
          {!isNotUsingGoogleTagManager && (
            <>
              <PrivacyNoticeTask
                description="Install the TrueVault CMP Template in Google Tag Manager"
                helpText={
                  <Paras>
                    <Para paragraph className="text-t1">
                      If you use GTM, install the TrueVault CMP Template and run it on the{" "}
                      <strong>Consent Initialization - All Pages</strong> trigger. The template
                      communicates the visitor's consent signals to GTM before it evaluates other
                      tags.{" "}
                      <ExternalLink
                        className="text-link"
                        href={GTM_TEMPLATE_SETUP_URL}
                        target="_blank"
                      >
                        Follow the template setup instructions
                      </ExternalLink>
                      .
                    </Para>
                  </Paras>
                }
              />
              <PrivacyNoticeTask
                description="Configure the consent requirements for your tags"
                helpText={
                  <Paras>
                    <Para paragraph className="text-t1">
                      TrueVault identified the categorized vendors shown below by scanning your
                      website. Review the corresponding tags in GTM and add each consent signal that
                      matches how the tag uses data. A tag can require more than one signal.
                    </Para>
                  </Paras>
                }
              >
                <Paras>
                  <ol className="custom custom-sublist p-0">
                    <PrivacyNoticeTask description="In the Workspace tab of Google Tag Manager, select Tags from the left navigation bar" />
                    <PrivacyNoticeTask description="Click on the tag you want to configure" />
                    <PrivacyNoticeTask description="Click Tag Configuration" />
                    <PrivacyNoticeTask description="Open Advanced Settings → Consent Settings" />
                    <PrivacyNoticeTask description="Select Require additional consent for tag to fire, then Add required consent" />
                    <ConsentSignalTask
                      title="Advertising"
                      signal="ad_storage"
                      vendors={scanVendorListItems(advertisingVendors)}
                      completedVendorIds={completedVendorIdsFor(
                        DevInstructionsConsentManagementAdvertisingVendorIdsSlug,
                      )}
                      onToggleVendor={makeToggleVendor(
                        DevInstructionsConsentManagementAdvertisingVendorIdsSlug,
                      )}
                    />
                    <ConsentSignalTask
                      title="Analytics"
                      signal="analytics_storage"
                      vendors={scanVendorListItems(analyticsVendors)}
                      completedVendorIds={completedVendorIdsFor(
                        DevInstructionsConsentManagementAnalyticsVendorIdsSlug,
                      )}
                      onToggleVendor={makeToggleVendor(
                        DevInstructionsConsentManagementAnalyticsVendorIdsSlug,
                      )}
                    />
                    <ConsentSignalTask
                      title="Preferences"
                      signal="personalization_storage"
                      vendors={scanVendorListItems(preferencesVendors)}
                      completedVendorIds={completedVendorIdsFor(
                        DevInstructionsConsentManagementPreferencesVendorIdsSlug,
                      )}
                      onToggleVendor={makeToggleVendor(
                        DevInstructionsConsentManagementPreferencesVendorIdsSlug,
                      )}
                    />
                    <ConsentSignalTask
                      title="Data Selling, Sharing, or Targeted Advertising"
                      signal="tv_not_opted_out"
                      vendors={optOutVendorListItems}
                      completedVendorIds={completedVendorIdsFor(
                        DevInstructionsConsentManagementFinishedVendorIdsSlug,
                      )}
                      onToggleVendor={makeToggleVendor(
                        DevInstructionsConsentManagementFinishedVendorIdsSlug,
                      )}
                      helpText={
                        consentManagementNonGppVendors.length > 0 ? (
                          <>
                            These Data Map vendors either do not respect GPP/GPC opt-out signals or
                            their support is unknown. Add <code>tv_not_opted_out</code> to tags that
                            should stop when a consumer opts out.
                          </>
                        ) : undefined
                      }
                    />
                    <PrivacyNoticeTask description="Click Save" />
                  </ol>
                </Paras>
              </PrivacyNoticeTask>
              <PrivacyNoticeTask
                description='Add the "Consent Changed" event'
                helpText={
                  <Para paragraph className="text-t1">
                    Set up the <code>consentChoice</code> event so applicable page-level tags are
                    reevaluated as soon as a visitor changes their preferences.{" "}
                    <ExternalLink
                      className="text-link"
                      href={GTM_CONSENT_CHANGED_URL}
                      target="_blank"
                    >
                      Follow the Consent Changed instructions
                    </ExternalLink>
                    .
                  </Para>
                }
              />
              <PrivacyNoticeTask
                description="Test and publish your setup"
                helpText={
                  <Para paragraph className="text-t1">
                    Use GTM Preview to verify each consent state before publishing the container,
                    then confirm that tags follow the expected consent behavior.{" "}
                    <ExternalLink className="text-link" href={GTM_TESTING_URL} target="_blank">
                      Follow the GTM testing guide
                    </ExternalLink>
                    .
                  </Para>
                }
              />
            </>
          )}
        </ol>
      </div>

      <NoticeSubHeading>Other Consent Management Options</NoticeSubHeading>
      <Para paragraph className="text-t1">
        Having trouble with the above setup, or have more complex needs? Let's get in touch:{" "}
        <ExternalLink className="text-link" href="mailto:help@truevault.com" target="_blank">
          help@truevault.com
        </ExternalLink>
      </Para>
    </div>
  );
};

type ConsentManagementVendorListItem = {
  id: string;
  completionId?: string;
  name: string;
  logoUrl?: string;
};

const ConsentSignalTask = ({
  title,
  signal,
  vendors,
  completedVendorIds = [],
  onToggleVendor,
  helpText,
}: {
  title: string;
  signal: string;
  vendors: ConsentManagementVendorListItem[];
  completedVendorIds?: string[];
  onToggleVendor?: (vendorId: string) => Promise<void>;
  helpText?: React.ReactNode;
}) => {
  return (
    <PrivacyNoticeTask
      description={
        <>
          For <strong>{title}</strong> tags, require:
        </>
      }
      helpText={helpText}
    >
      <PrivacyNoticeActions
        content={<span className="text-t1 text-font-mono">{signal}</span>}
        actions={[getCopyAction(`copy-${signal}`, "copy text", signal)]}
      />
      {vendors.length > 0 && (
        <ConsentManagementVendorList
          label={`See ${title} vendors`}
          signal={signal}
          vendors={vendors}
          completedVendorIds={completedVendorIds}
          onToggleVendor={onToggleVendor}
        />
      )}
    </PrivacyNoticeTask>
  );
};

const ConsentManagementVendorList = ({
  label,
  signal,
  vendors,
  completedVendorIds,
  onToggleVendor,
}: {
  label: string;
  signal: string;
  vendors: ConsentManagementVendorListItem[];
  completedVendorIds: string[];
  onToggleVendor?: (vendorId: string) => Promise<void>;
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  return (
    <div className="tw-boundary">
      <div className="mt-4 max-w-[360px]">
        <button
          type="button"
          className="flex cursor-pointer items-center gap-3 border-0 bg-transparent p-0 text-[16px] font-medium text-blue-600"
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          {label}
          <span className={isCollapsed ? "" : "rotate-180"}>
            <svg
              width="20"
              height="10"
              viewBox="0 0 24 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22 1.775L12 11.775L2 1.775L3.775 0L12 8.225L20.225 0L22 1.775Z"
                fill="#007BBD"
              />
            </svg>
          </span>
        </button>
        {!isCollapsed &&
          vendors.map((vendor) => {
            const completionId = vendor.completionId ?? vendor.id;
            return (
              <div
                key={vendor.id}
                className="flex items-center gap-3 border-b border-solid border-gray-200 px-4 py-3"
              >
                {onToggleVendor && (
                  <SimpleCheckboxControlled
                    // The signal keeps DOM ids unique when a vendor appears in more than one list
                    name={`consent-management-${signal}-${vendor.id}`}
                    checked={completedVendorIds.includes(completionId)}
                    onChange={() => onToggleVendor(completionId)}
                  />
                )}
                {vendor.logoUrl && (
                  <img className="h-6 w-6" src={vendor.logoUrl} alt="" aria-hidden="true" />
                )}
                <div>{vendor.name}</div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

type InStoreNoticesProps = {
  privacyCenter: PublicPrivacyCenterDto;
};

const InStoreNotices = ({ privacyCenter }: InStoreNoticesProps) => {
  return (
    <div className="compliance-checklist--notices">
      <div className="compliance-checklist--notices-items">
        <ol className="custom p-0">
          <PrivacyNoticeTask
            description="Post California Privacy Notice at consumer-facing stores or offices"
            helpText={
              <>
                Post prominent signage directing consumers to the URL where your business's online
                California Privacy Notice can be found.
              </>
            }
          >
            <UrlNoticeAction
              url={PrivacyCenterUrls.getCAPrivacyNoticeUrl(privacyCenter)}
              label="California Privacy Notice"
            />
          </PrivacyNoticeTask>
          <PrivacyNoticeTask
            description="Add California Privacy Notice to any other offline points of collection"
            helpText={
              <>
                Add the California Privacy Notice URL or the actual text of the notice to all other
                offline places where your business collects information from consumers in
                California, such as on paper forms.
              </>
            }
          >
            <UrlNoticeAction
              url={PrivacyCenterUrls.getCAPrivacyNoticeUrl(privacyCenter)}
              label="California Privacy Notice"
            />
          </PrivacyNoticeTask>
        </ol>
      </div>
    </div>
  );
};

type EmploymentNoticesProps = {
  privacyCenter: PublicPrivacyCenterDto;

  employmentGroups: EmploymentGroupNotices[];
  hasJobApplicants: boolean;
};
const EmploymentNotices = ({
  privacyCenter,
  employmentGroups,
  hasJobApplicants,
}: EmploymentNoticesProps) => {
  return (
    <>
      <div className="compliance-checklist--notices">
        <div className="compliance-checklist--notices-items">
          <ol className="custom p-0">
            {employmentGroups.length > 0 && (
              <PrivacyNoticeTask
                description="Post each Employment Notice at point of collection"
                helpText={
                  <>
                    Include each notice at or before the point where your business collects personal
                    information from California employees, such as accompanying an offer letter;
                    included with onboarding materials; as an addendum or enclosure with a contract
                    (e.g., for contractors); or in an employee handbook.
                  </>
                }
              >
                {employmentGroups
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((cg) => {
                    return (
                      <UrlNoticeAction key={cg.name} url={cg.url} label={`Notice to ${cg.name}`} />
                    );
                  })}
              </PrivacyNoticeTask>
            )}
            {hasJobApplicants && (
              <PrivacyNoticeTask
                description="Link to Job Applicant Notice at point of collection"
                helpText={
                  <>
                    Add a "Notice to California Job Applicants" link where your business collects
                    personal information from job applicants in California, such as where it posts
                    hiring ads online.
                  </>
                }
              >
                <UrlNoticeAction
                  url={PrivacyCenterUrls.getJobApplicantsNoticeUrl(privacyCenter)}
                  label="Notice to California Job Applicants"
                />
              </PrivacyNoticeTask>
            )}
          </ol>
        </div>
      </div>
    </>
  );
};

type WebInstructionsProps = {
  privacyCenter: PublicPrivacyCenterDto;
  logoLinkUrl: string;
  isSelling: boolean;
  isSharing: boolean;
  isGoogleAnalytics: boolean;
  isGAMeasurementIdKnown: boolean;
  showFinancialIncentive: boolean;
  canShowGANudge: boolean;
};

const CONSENT_MANAGEMENT_GETTING_STARTED_URL =
  "https://developers.truevault.com/docs/consent-management/getting-started";
const GTM_SETUP_OVERVIEW_URL =
  "https://developers.truevault.com/docs/google-tag-manager/setting-up-the-consent-banner";
const GTM_TEMPLATE_SETUP_URL =
  "https://developers.truevault.com/docs/google-tag-manager/setup-truevault-cmp-template";
const GTM_CONSENT_CHANGED_URL =
  "https://developers.truevault.com/docs/google-tag-manager/add-consent-changed-event";
const GTM_TESTING_URL =
  "https://developers.truevault.com/docs/google-tag-manager/testing-google-tag-manager-setup";

const SHOPIFY_CONSENT_HELPSCOUT_URL: string =
  "https://developers.truevault.com/docs/shopify/shopify-app-pixel-consent";
const BIGCOMMERCE_CONSENT_URL =
  "https://developers.truevault.com/docs/bigcommerce/bigcommerce-consent";
const CONSENT_MANAGEMENT_WITHOUT_GTM_URL =
  "https://developers.truevault.com/docs/consent-management-without-gtm";

const WebInstructions = ({
  privacyCenter,
  logoLinkUrl,
  isSelling,
  isSharing,
  isGoogleAnalytics,
  isGAMeasurementIdKnown,
  showFinancialIncentive,
  canShowGANudge,
}: WebInstructionsProps) => {
  const polarisJsHtml = getPolarisHtml(privacyCenter.id);

  return (
    <div className="compliance-checklist--notices">
      <div className="compliance-checklist--notices-items">
        <ol className="custom p-0">
          <PrivacyNoticeTask
            description="Update all Privacy Policy links on your website"
            helpText="Edit all Privacy Policy links on your business's website so that they link to the new Privacy Policy."
          >
            <UrlNoticeAction
              id="copy-privacy-policy-web"
              url={PrivacyCenterUrls.getPrivacyPolicyUrl(privacyCenter)}
            />
          </PrivacyNoticeTask>
          <PrivacyNoticeTask
            description="Add Polaris Client-Side JavaScript to your website"
            helpText={
              <>
                <div className="text-t1">
                  Add the following JavaScript in the top of the <code>&#60;head&#62;</code> element
                  of your business's website. It must be the{" "}
                  <strong>first functional script</strong> (important: if you use Google Tag
                  Manager, place the Polaris script above the GTM script).
                </div>

                <div className="text-t1 mt-lg">
                  This script is required for Polaris to function properly. It performs several
                  critical functions including limiting the visibility of state-specific links and
                  notices on your business's website so that visitors from states with privacy laws
                  will see the notices that apply to them.
                </div>

                {isGoogleAnalytics && (
                  <>
                    {!isGAMeasurementIdKnown && canShowGANudge && (
                      <div className="text-t1 mt-lg">
                        <p>
                          <Callout variant={CalloutVariant.Yellow} icon={InfoIcon}>
                            <span>
                              Your data map indicates that you are using Google Analytics. To allow
                              the Google Analytics opt-out integration to work on your website, you
                              need to add your Google Analytics Measurement ID on the
                            </span>{" "}
                            <Link to={Routes.privacyCenter.integrations}>Integrations</Link>{" "}
                            <span>page</span>.
                          </Callout>
                        </p>
                      </div>
                    )}
                    <div className="text-t1 mt-sm">
                      <strong>Note:</strong> To ensure that Polaris can evaluate GA opt-outs prior
                      to the Google Analytics script getting executed, do not add the "defer" or
                      "async" attributes to the <strong>polaris.js</strong> script.
                    </div>
                  </>
                )}
              </>
            }
          >
            <PrivacyNoticeActions
              content={
                <span className="text-t1 text-font-mono text-pre-wrap">{polarisJsHtml}</span>
              }
              actions={[
                getCopyAction("add-dns-link-to-website-script", "copy HTML", polarisJsHtml),
              ]}
            />
          </PrivacyNoticeTask>
          <PrivacyNoticeTask
            description="Add California Privacy Notice to website footer"
            helpText={
              <>
                Place the script below where you'd like the link to the{" "}
                <ExternalLink
                  className="text-link text-medium"
                  href={PrivacyCenterUrls.getCAPrivacyNoticeUrl(privacyCenter)}
                  target="_blank"
                >
                  California Privacy Notice
                </ExternalLink>{" "}
                to appear. The link is typically placed in the website footer near the Privacy
                Policy link.
              </>
            }
          >
            <PrivacyNoticeActions
              content={
                <span className="text-t1 text-font-mono">
                  {getCaPrivacyNoticeHtml(PrivacyCenterUrls.getCAPrivacyNoticeUrl(privacyCenter))}
                </span>
              }
              actions={[
                getCopyAction(
                  "add-ca-privacy-notice-website-home-script",
                  "copy HTML",
                  getCaPrivacyNoticeHtml(PrivacyCenterUrls.getCAPrivacyNoticeUrl(privacyCenter)),
                ),
              ]}
            />
          </PrivacyNoticeTask>
          <PrivacyNoticeTask
            description="Add California Privacy Notice to any other points of collection on website"
            helpText={
              <>
                Points on a website where a{" "}
                <ExternalLink
                  className="text-link text-medium"
                  href={PrivacyCenterUrls.getCAPrivacyNoticeUrl(privacyCenter)}
                  target="_blank"
                >
                  California Privacy Notice
                </ExternalLink>{" "}
                link should be added include pages where people can sign up for emails, create an
                account, make a purchase (checkout), or fill out a form.
              </>
            }
          >
            <PrivacyNoticeActions
              content={
                <span className="text-t1 text-font-mono">
                  {getCaPrivacyNoticeHtml(PrivacyCenterUrls.getCAPrivacyNoticeUrl(privacyCenter))}
                </span>
              }
              actions={[
                getCopyAction(
                  "add-ca-privacy-notice-website-collection-script",
                  "copy HTML",
                  getCaPrivacyNoticeHtml(PrivacyCenterUrls.getCAPrivacyNoticeUrl(privacyCenter)),
                ),
              ]}
            />
          </PrivacyNoticeTask>

          {showFinancialIncentive && (
            <PrivacyNoticeTask
              description="Add Notice of Financial Incentive to opt-in points"
              helpText={
                "Post the Notice of Financial Incentive link at all points on your website where a consumer can opt-in to the financial incentive or price or service difference described in the Notice."
              }
            >
              <PrivacyNoticeActions
                content={
                  <span className="text-t1 text-mono">
                    {getIncentiveNoticeHtml(privacyCenter.url)}
                  </span>
                }
                actions={[
                  getCopyAction(
                    "add-ca-privacy-notice-website-collection-script",
                    "copy HTML",
                    getIncentiveNoticeHtml(privacyCenter.url),
                  ),
                ]}
              />
            </PrivacyNoticeTask>
          )}
          <SellingOrSharingOptOut
            privacyCenter={privacyCenter}
            isSelling={isSelling}
            isSharing={isSharing}
          />
          <PrivacyNoticeTask
            description="Verify Privacy Policy link displays on a mobile device"
            helpText={
              "Visit your business's website on a mobile device and check that the Privacy " +
              "Policy link is visible."
            }
          />
          <PrivacyNoticeTask
            description="(Optional) Add footer link to cookie pop-up"
            helpText={
              <>
                You can optionally add the link text "Cookie Preferences" to your website footer —
                for example, below the "Your Privacy Choices" link — using the following URL:
              </>
            }
          >
            <PrivacyNoticeActions
              content={
                <span className="text-t1 text-font-mono">
                  {getCookiePreferencesHtml(logoLinkUrl)}
                </span>
              }
              actions={[
                getCopyAction(
                  "add-cookie-preferences-link-script",
                  "copy HTML",
                  getCookiePreferencesHtml(logoLinkUrl),
                ),
              ]}
            />
          </PrivacyNoticeTask>
        </ol>
      </div>
    </div>
  );
};

const showOptOutNotice = (isSelling: boolean, isSharing: boolean) => isSelling || isSharing;

const optOutLabel = () => "Your Privacy Choices";

const SellingOrSharingOptOut = ({
  privacyCenter,
  isSelling,
  isSharing,
}: {
  privacyCenter: PublicPrivacyCenterDto;
  isSelling: boolean;
  isSharing: boolean;
}) => {
  const [html, setHtml] = useState(null);

  if (!isSelling && !isSharing) {
    return null;
  }

  const linkCopy = optOutLabel();
  const link = PrivacyCenterUrls.getOptOutUrl(privacyCenter, isSelling, isSharing);

  return (
    <PrivacyNoticeTask
      description="Add Opt-Out Notice to website footer"
      helpText={
        <>
          Place the link below where you’d like the{" "}
          <ExternalLink className="text-link text-medium" href={link} target="_blank">
            {linkCopy}
          </ExternalLink>{" "}
          link to appear. The link is typically placed in the footer near the Privacy Policy link.
          You may change the style of the link, but do not change the link text.
          <div className="text-t1 mt-lg">
            <Callout variant={CalloutVariant.Yellow} icon={InfoIcon}>
              <strong>The “Your Privacy Choices” link may disappear during testing.</strong> Keep
              the <code>truevault-polaris-optout</code> class on this link. The CMP uses the class
              to show the link only in states where your configuration makes it relevant. If the
              link is hidden while testing from another state, the integration is working as
              intended. Do not remove the class.
            </Callout>
          </div>
        </>
      }
    >
      <OptOutIconSelector linkUrl={link} linkCopy={linkCopy} onUpdate={setHtml} />
      <PrivacyNoticeActions
        content={<span className="text-t1 text-font-mono text-pre-wrap">{html}</span>}
        actions={[getCopyAction("add-opt-out-notice-website-home-script", "copy HTML", html)]}
      />
    </PrivacyNoticeTask>
  );
};

const POLARIS_JS_HTML_START = "<!-- Start of TrueVault Code Block -->\n";

const getPolarisJSHTMLEnd = (privacyCenterId: string): string => {
  return (
    `<script src="https://polaris.truevaultcdn.com/static/pc/${privacyCenterId}/polaris.js">\n</script>\n` +
    `<!-- End of TrueVault Code Block -->`
  );
};

const getPolarisHtml = (privacyCenterId: string): string => {
  return POLARIS_JS_HTML_START + getPolarisJSHTMLEnd(privacyCenterId);
};

const getCaPrivacyNoticeHtml = (url: string) =>
  `<a class="truevault-polaris-privacy-notice" href="${url}" rel="noopener noreferrer" hidden>California Privacy Notice</a>`;

const getCookiePreferencesHtml = (url: string) =>
  `<a href="https://${removeUrlProtocol(
    url,
  )}?polaris.openConsentManager=true">Cookie Preferences</a>`;

export const getIncentiveNoticeHtml = (url: string) =>
  `<a class="truevault-polaris-privacy-notice" href="${url}/privacy-policy#financial-incentive" rel="noopener noreferrer" hidden>Notice of Financial Incentive</a>`;

const getMobilePrivacyNoticesText = (
  caPrivacyNoticeUrl: string,
  privacyCenter: PublicPrivacyCenterDto,
  isSelling: boolean,
  isSharing: boolean,
) => {
  const caNotice = `California Privacy Notice:\n${caPrivacyNoticeUrl}\n\n`;

  const showOptOut = showOptOutNotice(isSelling, isSharing);
  const label = optOutLabel();
  const link = PrivacyCenterUrls.getOptOutUrl(privacyCenter, isSelling, isSharing);
  const dnsNotice = showOptOut ? `${label}:\n${link}\n\n` : "";
  return `${caNotice}${dnsNotice}See our Privacy Policy to access ${
    showOptOut ? "these links" : "this link"
  }.`;
};

export type PrivacyNoticeChecklistNoticeItemAction = {
  id: string;
  content: React.ReactNode;
  onClick: () => void;
};

export const getCopyAction = (
  id: string | undefined,
  text: string,
  copyText: string,
): PrivacyNoticeChecklistNoticeItemAction => {
  return {
    id,
    content: (
      <>
        <span className="compliance-checklist--copy-done">
          <Done />{" "}
        </span>
        <span className="compliance-checklist--copy-copy">
          <FileCopyOutlined />
        </span>
        {text}
      </>
    ),
    onClick: () => {
      navigator.clipboard.writeText(copyText);
      document.getElementById(id)?.classList.add("copy-done");
      setTimeout(() => document.getElementById(id)?.classList.remove("copy-done"), 2000);
    },
  };
};

const UrlNoticeAction = ({ url, label, id }: { url: string; label?: string; id?: string }) => {
  const actions = [];

  actions.push(getCopyAction(id ?? `copy-${url}`, "copy URL", url));

  return (
    <PrivacyNoticeActions
      content={<span className="text-t1 text-mono">{label || url}</span>}
      actions={actions}
    />
  );
};

interface PrivacyNoticeTaskProps {
  description: string | React.ReactNode;
  helpText?: string | React.ReactNode;
  children?: React.ReactNode | React.ReactNodeArray;
}
const PrivacyNoticeTask = ({ description, helpText, children }: PrivacyNoticeTaskProps) => {
  return (
    <li className="compliance-checklist--notices-item">
      <div className="compliance-checklist--notices-item-content">
        <div className="compliance-checklist--notices-item-description">{description}</div>
        <div className="compliance-checklist--notices-item-help-text">{helpText}</div>
        {children && <div className="compliance-checklist--notices-item-actions">{children}</div>}
      </div>
    </li>
  );
};

const NoticeSubHeading = ({ children }: { children?: React.ReactNode }) => (
  <h4 className={`text-t1 text-spacing-spread text-muted text-weight-regular`}>{children}</h4>
);
